import React, { useState, useEffect } from 'react'
import { X, Save, Menu, AlertTriangle, Search } from 'lucide-react'
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query'
import { menuService, MenuItem, CreateMenuData } from '../../services/menuService'
import { roleService } from '../../services/roleService'
import toast from 'react-hot-toast'

interface MenuFormData {
  name: string
  path: string
  icon: string
  parent_id: number | null
  sort_order: number
  is_active: boolean
  roles: string[]
}

interface MenuFormModalProps {
  isOpen: boolean
  onClose: () => void
  menu?: MenuItem | null
}

// Lista de iconos disponibles de Lucide React
const AVAILABLE_ICONS = [
  'Home', 'Dashboard', 'Users', 'Shield', 'Settings', 'Cog', 'Menu',
  'FileText', 'Database', 'Server', 'Activity', 'BarChart', 'PieChart',
  'Calendar', 'Mail', 'Bell', 'Search', 'Filter', 'Download', 'Upload',
  'Plus', 'Edit', 'Trash2', 'Eye', 'EyeOff', 'Lock', 'Unlock',
  'Star', 'Heart', 'Bookmark', 'Tag', 'Flag', 'MapPin', 'Globe',
  'Phone', 'MessageCircle', 'Video', 'Image', 'File', 'Folder',
  'Archive', 'Package', 'ShoppingCart', 'CreditCard', 'DollarSign',
  'TrendingUp', 'TrendingDown', 'Zap', 'Cpu', 'HardDrive', 'Wifi',
  'Bluetooth', 'Battery', 'Power', 'RefreshCw', 'RotateCcw',
  'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'ChevronLeft',
  'ChevronRight', 'ChevronUp', 'ChevronDown', 'MoreHorizontal', 'MoreVertical'
]

const MenuFormModal: React.FC<MenuFormModalProps> = ({ isOpen, onClose, menu }) => {
  const queryClient = useQueryClient()
  const isEditing = !!menu
  
  const [formData, setFormData] = useState<MenuFormData>({
    name: '',
    path: '',
    icon: 'Menu',
    parent_id: null,
    sort_order: 1,
    is_active: true,
    roles: []
  })

  const [errors, setErrors] = useState<Record<string, string>>({})
  const [iconSearch, setIconSearch] = useState('')
  const [showIconPicker, setShowIconPicker] = useState(false)

  // Cargar roles disponibles
  const { data: roles } = useQuery({
    queryKey: ['roles'],
    queryFn: roleService.getRoles
  })

  // Cargar menús padre disponibles
  const { data: availableParents } = useQuery({
    queryKey: ['available-parents', menu?.id],
    queryFn: () => menuService.getAvailableParents(menu?.id),
    enabled: isOpen
  })

  // Reset form when modal opens/closes or menu changes
  useEffect(() => {
    if (isOpen && menu) {
      setFormData({
        name: menu.name,
        path: menu.path,
        icon: menu.icon || 'Menu',
        parent_id: menu.parent_id ?? null,
        sort_order: menu.sort_order ?? 1,
        is_active: menu.is_active ?? true,
        roles: menu.roles || []
      })
    } else if (isOpen) {
      setFormData({
        name: '',
        path: '',
        icon: 'Menu',
        parent_id: null,
        sort_order: 1,
        is_active: true,
        roles: []
      })
    }
    setErrors({})
    setIconSearch('')
  }, [isOpen, menu])

  const createMenuMutation = useMutation({
    mutationFn: (data: CreateMenuData) => menuService.createMenu(data),
    onSuccess: () => {
      queryClient.invalidateQueries(['menus-test-data'])
      queryClient.invalidateQueries(['user-menus-sidebar'])
      toast.success('Menú creado exitosamente')
      onClose()
    },
    onError: (error: any) => {
      toast.error(error.message || 'Error al crear menú')
    }
  })

  const updateMenuMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<CreateMenuData> }) =>
      menuService.updateMenu(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['menus-test-data'])
      queryClient.invalidateQueries(['user-menus-sidebar'])
      toast.success('Menú actualizado exitosamente')
      onClose()
    },
    onError: (error: any) => {
      toast.error(error.message || 'Error al actualizar menú')
    }
  })

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'El nombre es requerido'
    } else if (formData.name.length < 2) {
      newErrors.name = 'El nombre debe tener al menos 2 caracteres'
    }

    // No marcamos error si la ruta no comienza con '/':
    // se normaliza automáticamente al enviar.

    if (formData.sort_order < 1) {
      newErrors.sort_order = 'El orden debe ser mayor a 0'
    }

    if (formData.roles.length === 0) {
      newErrors.roles = 'Debe seleccionar al menos un rol'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  // Normaliza el path para que siempre comience con un '/'
  const normalizePath = (p: string): string => {
    const t = (p || '').trim()
    if (!t) return ''
    return '/' + t.replace(/^\/+/, '')
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) {
      return
    }

    // Mapear nombres de roles seleccionados a IDs esperados por el backend
    const selectedRoleIds = (roles || [])
      .filter(r => formData.roles.includes(r.name))
      .map(r => r.id)

    const submitData: CreateMenuData = {
      name: formData.name.trim(),
      path: normalizePath(formData.path),
      icon: formData.icon,
      // Para crear: omitir parent si es raíz (undefined)
      parent_id: isEditing ? (formData.parent_id as any) : (formData.parent_id ?? undefined),
      sort_order: formData.sort_order,
      is_active: formData.is_active,
      roles: formData.roles,
      roleIds: selectedRoleIds
    }

    if (isEditing && menu) {
      // En edición: permitir enviar null explícito para dejar en raíz
      const updateData = { ...submitData, parent_id: formData.parent_id as any }
      updateMenuMutation.mutate({ id: menu.id, data: updateData })
    } else {
      createMenuMutation.mutate(submitData)
    }
  }

  const handleInputChange = (field: keyof MenuFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
    
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }))
    }
  }

  const handleRoleToggle = (roleName: string) => {
    const newRoles = formData.roles.includes(roleName)
      ? formData.roles.filter(r => r !== roleName)
      : [...formData.roles, roleName]
    
    handleInputChange('roles', newRoles)
  }

  const filteredIcons = AVAILABLE_ICONS.filter(icon =>
    icon.toLowerCase().includes(iconSearch.toLowerCase())
  )

  const isSubmitting = createMenuMutation.isLoading || updateMenuMutation.isLoading

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center">
            <div className="h-8 w-8 bg-primary-100 rounded-lg flex items-center justify-center mr-3">
              <Menu className="h-5 w-5 text-primary-600" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                {isEditing ? 'Editar Menú' : 'Crear Nuevo Menú'}
              </h2>
              <p className="text-sm text-gray-500">
                {isEditing ? 'Modifica los datos del menú' : 'Configura un nuevo elemento del menú'}
              </p>
            </div>
          </div>
          <button 
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6" style={{ maxHeight: 'calc(90vh - 180px)' }}>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del Menú *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleInputChange('name', e.target.value)}
                  className={`input w-full ${errors.name ? 'border-red-300' : ''}`}
                  placeholder="Dashboard, Usuarios, etc."
                />
                {errors.name && (
                  <p className="mt-1 text-xs text-red-600">{errors.name}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ruta
                </label>
                <input
                  type="text"
                  value={formData.path}
                  onChange={(e) => handleInputChange('path', e.target.value)}
                  className={`input w-full ${errors.path ? 'border-red-300' : ''}`}
                  placeholder="/dashboard, /users, etc."
                />
                {errors.path && (
                  <p className="mt-1 text-xs text-red-600">{errors.path}</p>
                )}
              </div>
            </div>

            {/* Icon and Parent */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ícono
                </label>
                <div className="relative">
                  <button
                    type="button"
                    onClick={() => setShowIconPicker(!showIconPicker)}
                    className="input w-full flex items-center justify-between"
                  >
                    <span className="flex items-center">
                      <span className="mr-2">{formData.icon}</span>
                      {formData.icon}
                    </span>
                    <Search className="h-4 w-4 text-gray-400" />
                  </button>
                  
                  {showIconPicker && (
                    <div className="absolute z-10 mt-1 w-full bg-white border border-gray-300 rounded-md shadow-lg">
                      <div className="p-3 border-b">
                        <input
                          type="text"
                          placeholder="Buscar ícono..."
                          value={iconSearch}
                          onChange={(e) => setIconSearch(e.target.value)}
                          className="input w-full"
                        />
                      </div>
                      <div className="max-h-40 overflow-y-auto p-2">
                        <div className="grid grid-cols-4 gap-2">
                          {filteredIcons.map((icon) => (
                            <button
                              key={icon}
                              type="button"
                              onClick={() => {
                                handleInputChange('icon', icon)
                                setShowIconPicker(false)
                                setIconSearch('')
                              }}
                              className={`p-2 text-center hover:bg-gray-100 rounded ${
                                formData.icon === icon ? 'bg-primary-100 text-primary-600' : ''
                              }`}
                            >
                              <div className="text-sm">{icon}</div>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Menú Padre
                </label>
                <select
                  value={formData.parent_id || ''}
                  onChange={(e) => handleInputChange('parent_id', e.target.value ? parseInt(e.target.value) : null)}
                  className="input w-full"
                >
                  <option value="">Sin menú padre (menú principal)</option>
                  {availableParents?.map((parent: any) => {
                    const level = parent.level ?? 0
                    const prefix = level > 0 ? '— '.repeat(level) : ''
                    return (
                      <option key={parent.id} value={parent.id}>
                        {prefix}{parent.name}
                      </option>
                    )
                  })}
                </select>
              </div>
            </div>

            {/* Order and Status */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Orden
                </label>
                <input
                  type="number"
                  min="1"
                  value={formData.sort_order}
                  onChange={(e) => handleInputChange('sort_order', parseInt(e.target.value) || 1)}
                  className={`input w-full ${errors.sort_order ? 'border-red-300' : ''}`}
                />
                {errors.sort_order && (
                  <p className="mt-1 text-xs text-red-600">{errors.sort_order}</p>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Estado
                </label>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="is_active"
                    checked={formData.is_active}
                    onChange={(e) => handleInputChange('is_active', e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                  />
                  <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
                    Menú activo
                  </label>
                </div>
              </div>
            </div>

            {/* Roles Access */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Roles con Acceso *
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {roles?.map((role) => (
                  <label
                    key={role.id}
                    className="flex items-center cursor-pointer p-2 border border-gray-200 rounded hover:bg-gray-50"
                  >
                    <input
                      type="checkbox"
                      checked={formData.roles.includes(role.name)}
                      onChange={() => handleRoleToggle(role.name)}
                      className="rounded border-gray-300 text-primary-600 shadow-sm focus:border-primary-300 focus:ring focus:ring-primary-200 focus:ring-opacity-50"
                    />
                    <span className="ml-2 text-sm text-gray-700">{role.name}</span>
                  </label>
                ))}
              </div>
              {errors.roles && (
                <p className="mt-1 text-xs text-red-600 flex items-center">
                  <AlertTriangle className="h-3 w-3 mr-1" />
                  {errors.roles}
                </p>
              )}
            </div>
          </form>
        </div>

        {/* Footer */}
        <div className="flex justify-end items-center p-6 border-t border-gray-200 bg-gradient-to-r from-gray-50 to-gray-100">
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="btn btn-secondary-static btn-md"
            >
              Cancelar
            </button>
            
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="btn btn-primary-static btn-md"
            >
              {isSubmitting ? (
                <div className="flex items-center">
                  <div className="spinner w-4 h-4 mr-2"></div>
                  {isEditing ? 'Actualizando...' : 'Creando...'}
                </div>
              ) : (
                <>
                  <Save className="h-4 w-4" />
                  {isEditing ? 'Actualizar Menú' : 'Crear Menú'}
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export default MenuFormModal
